'use strict';

/**
 * Storefront API 商品カード 自動初期化スクリプト
 *
 * 使い方:
 *   1. テーマの <head> に style.css を読み込む
 *   2. テーマの </body> 直前に script.js を読み込む
 *   3. ブログ記事に <div class="hjw-card" data-product-id="..."></div> を貼り付ける
 *
 * 全クラス名に hjw- (Harada Jewelry Widget) プレフィックス付き。
 * ページ内に複数カードがあれば、それぞれ独立して初期化される。
 */
(function () {
    // === Storefront API 設定 ===
    const CONFIG = {
        storeDomain: 'jewelry-harada.myshopify.com',
        storefrontAccessToken: '8f15a4d0a644c21d055009bc0ed847b7',
        apiVersion: '2025-10',
        inquiryBaseUrl: 'https://harada-jwg.com/pages/contact',
    };

    const TAX_RATE = typeof Theme !== 'undefined' ? Theme.taxRate : 0.1;
    const PAYMENT_TERMS = [36, 60, 84, 100];
    const MIN_MONTHLY_PAYMENT = 3000;

    // === GraphQL クエリ ===
    const PRODUCT_BY_ID_QUERY = `
        query ProductById($id: ID!) {
            product(id: $id) {
                id title handle description descriptionHtml productType tags
                images(first: 5) { edges { node { url altText width height } } }
                priceRange {
                    minVariantPrice { amount currencyCode }
                    maxVariantPrice { amount currencyCode }
                }
                variants(first: 10) { edges { node { id title availableForSale price { amount currencyCode } } } }
            }
        }
    `;

    const PRODUCT_BY_HANDLE_QUERY = `
        query ProductByHandle($handle: String!) {
            productByHandle(handle: $handle) {
                id title handle description descriptionHtml productType tags
                images(first: 5) { edges { node { url altText width height } } }
                priceRange {
                    minVariantPrice { amount currencyCode }
                    maxVariantPrice { amount currencyCode }
                }
                variants(first: 10) { edges { node { id title availableForSale price { amount currencyCode } } } }
            }
        }
    `;

    // === API通信 ===

    async function storefrontFetch(query, variables) {
        const endpoint = `https://${CONFIG.storeDomain}/api/${CONFIG.apiVersion}/graphql.json`;
        const response = await fetch(endpoint, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Shopify-Storefront-Access-Token': CONFIG.storefrontAccessToken,
            },
            body: JSON.stringify({ query, variables }),
        });

        if (!response.ok) {
            throw new Error(`API通信エラー: ${response.status} ${response.statusText}`);
        }

        const json = await response.json();
        if (json.errors) {
            throw new Error(`GraphQLエラー: ${json.errors.map((e) => e.message).join(', ')}`);
        }
        return json.data;
    }

    async function fetchProduct(identifier) {
        if (identifier.startsWith('gid://')) {
            const data = await storefrontFetch(PRODUCT_BY_ID_QUERY, { id: identifier });
            return data.product;
        }
        const data = await storefrontFetch(PRODUCT_BY_HANDLE_QUERY, { handle: identifier });
        return data.productByHandle;
    }

    // === 支払いシミュレーション ===

    function calculatePayments(totalPrice, term) {
        if (totalPrice <= 0 || term <= 0) return { monthly: 0, first: 0 };
        const monthly = Math.floor(totalPrice / term / 100) * 100;
        const first = totalPrice - monthly * (term - 1);
        return { monthly, first };
    }

    function getValidPlans(productPrice) {
        return PAYMENT_TERMS
            .map((term) => {
                const p = calculatePayments(productPrice, term);
                return { term, monthly: p.monthly, first: p.first };
            })
            .filter((plan) => plan.monthly >= MIN_MONTHLY_PAYMENT);
    }

    // === HTMLエスケープ ===

    function escapeHtml(str) {
        if (!str) return '';
        return str
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');
    }

    // === URL生成 ===

    function buildInquiryUrl(productName) {
        if (!productName) return '#';
        try {
            const url = new URL(CONFIG.inquiryBaseUrl, window.location.origin);
            url.searchParams.set('contact[お問い合わせ商品]', productName);
            return url.toString();
        } catch (_e) {
            return '#';
        }
    }

    // === モーダル（ページ内で1つだけ動的生成） ===

    let $modal = null;
    let $tableBody = null;

    function ensureModal() {
        if ($modal) return;

        $modal = document.createElement('div');
        $modal.className = 'hjw-modal';
        $modal.innerHTML = `
            <div class="hjw-modal__content">
                <button type="button" class="hjw-modal__close">&times;</button>
                <h2 class="hjw-modal__title">お支払いシミュレーション</h2>
                <p class="hjw-modal__subtitle">金利手数料はどのお支払回数でも<span class="hjw-modal__free">無料</span>です</p>
                <table class="hjw-modal__table">
                    <thead>
                        <tr><th>回数</th><th>お支払い金額</th><th>初回お支払い金額</th></tr>
                    </thead>
                    <tbody></tbody>
                </table>
                <div class="hjw-modal__footer">
                    <form action="https://order.orico.co.jp/weborder/start.do" method="post" target="_blank">
                        <input name="DESCRIPTION_ID" type="hidden" value="S000">
                        <input name="KAMEI_NO" type="hidden" value="15683006">
                        <input name="KYAKU_SYU" type="hidden" value="900">
                        <input type="submit" value="オリコ支払いシミュレーションへ（公式サイト）" class="hjw-modal__orico-btn">
                    </form>
                </div>
            </div>`;

        document.body.appendChild($modal);
        $tableBody = $modal.querySelector('tbody');

        $modal.querySelector('.hjw-modal__close').addEventListener('click', closeModal);
        $modal.addEventListener('click', (e) => {
            if (e.target === $modal) closeModal();
        });
    }

    function renderSimulationTable(plans) {
        $tableBody.textContent = '';

        if (plans.length === 0) {
            const $row = document.createElement('tr');
            const $cell = document.createElement('td');
            $cell.setAttribute('colspan', '3');
            $cell.className = 'hjw-modal__cell--empty';
            $cell.textContent = '月々のお支払い金額が3,000円未満となるため、該当する支払いプランはありません。';
            $row.appendChild($cell);
            $tableBody.appendChild($row);
            return;
        }

        plans.forEach((plan) => {
            const $row = document.createElement('tr');

            const $term = document.createElement('td');
            $term.textContent = `${plan.term}回`;

            const $monthly = document.createElement('td');
            $monthly.className = 'hjw-modal__cell--amount';
            $monthly.textContent = `${plan.monthly.toLocaleString()}円`;

            const $first = document.createElement('td');
            $first.textContent = `${plan.first.toLocaleString()}円`;

            $row.appendChild($term);
            $row.appendChild($monthly);
            $row.appendChild($first);
            $tableBody.appendChild($row);
        });
    }

    function openModal(plans) {
        ensureModal();
        renderSimulationTable(plans);
        $modal.classList.add('is-visible');
        document.body.classList.add('hjw-no-scroll');
    }

    function closeModal() {
        if (!$modal) return;
        $modal.classList.remove('is-visible');
        document.body.classList.remove('hjw-no-scroll');
    }

    // === カード初期化（内部HTMLを全て動的生成） ===

    async function initCard($card) {
        const identifier = $card.dataset.productId;
        if (!identifier) return;

        // ローディング表示
        $card.innerHTML = '<div class="hjw-card__loading">商品情報を取得中...</div>';

        try {
            const product = await fetchProduct(identifier);

            if (!product) {
                $card.innerHTML = '<div class="hjw-card__body"><p class="hjw-card__error">商品が見つかりませんでした。</p></div>';
                return;
            }

            // 価格（税込）
            const priceExcludingTax = Number(product.priceRange.minVariantPrice.amount);
            const priceAmount = Math.round(priceExcludingTax * (1 + TAX_RATE));
            const safeHandle = encodeURIComponent(product.handle);
            const productUrl = `https://${CONFIG.storeDomain}/products/${safeHandle}`;
            const inquiryUrl = buildInquiryUrl(product.title);
            const firstImage = product.images.edges[0]?.node;

            // エスケープ済みの値
            const safeTitle = escapeHtml(product.title);
            const safeImgUrl = firstImage ? escapeHtml(firstImage.url) : '';
            const safeImgAlt = firstImage ? escapeHtml(firstImage.altText || product.title) : '';

            // 支払いシミュレーション
            const validPlans = getValidPlans(priceAmount);
            let simulationHtml = '';
            let simulationBtnHtml = '';

            if (priceAmount > 0 && validPlans.length > 0) {
                const maxTermPlan = [...validPlans].sort((a, b) => b.term - a.term)[0];
                simulationHtml = `
                    <div class="hjw-card__simulation">
                        <p class="hjw-card__simulation-label">
                            無金利分割払いなら月々<span>${maxTermPlan.monthly.toLocaleString()}</span>円から
                        </p>
                        <p class="hjw-card__simulation-note">※${maxTermPlan.term}回払いの場合。初回のみ${maxTermPlan.first.toLocaleString()}円</p>
                    </div>`;
                simulationBtnHtml = `<button type="button" class="hjw-card__btn hjw-card__btn--simulation" data-hjw="open-modal">お支払いシミュレーションを見る</button>`;
            }

            // カード内HTML生成
            $card.innerHTML = `
                <div class="hjw-card__image" style="cursor:pointer;">
                    <img src="${safeImgUrl}" alt="${safeImgAlt}" width="400" height="400">
                </div>
                <div class="hjw-card__body">
                    <h2 class="hjw-card__title">${safeTitle}</h2>
                    <p class="hjw-card__price">¥${priceAmount.toLocaleString()}<span class="hjw-card__tax">(税込)</span></p>
                    ${simulationHtml}
                    <div class="hjw-card__actions">
                        <a href="${escapeHtml(inquiryUrl)}" class="hjw-card__btn hjw-card__btn--inquiry">お問合せ</a>
                        ${simulationBtnHtml}
                    </div>
                </div>`;

            // 画像クリックで商品ページへ
            $card.querySelector('.hjw-card__image').addEventListener('click', () => {
                window.open(productUrl, '_blank');
            });

            // シミュレーションボタン
            const $simBtn = $card.querySelector('[data-hjw="open-modal"]');
            if ($simBtn) {
                $simBtn.addEventListener('click', () => openModal(validPlans));
            }

        } catch (error) {
            console.error(error);
            $card.innerHTML = '<div class="hjw-card__body"><p class="hjw-card__error">商品情報の取得に失敗しました</p></div>';
        }
    }

    // === ページ内の全カードを検出して初期化 ===

    function initAll() {
        const cards = document.querySelectorAll('.hjw-card[data-product-id]');
        cards.forEach((card) => initCard(card));
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initAll);
    } else {
        initAll();
    }
})();
